/**
 * Multi-Cashier Shifts — admin settings panel (Phase 1).
 *
 * Lives inside SettingsPage. Lets an admin toggle the org-level feature flag
 * with the same guardrails the backend enforces (cannot disable while shifts
 * are open). Renders a friendly status card explaining what happens at each
 * state.
 */
import { useEffect, useState } from 'react';
import { api } from '../../contexts/AuthContext';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { ClipboardCheck, AlertTriangle, Info } from 'lucide-react';
import { toast } from 'sonner';

export default function ShiftsSettingsPanel() {
  const [loading, setLoading] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await api.get('/settings/multi-cashier-shifts');
      setEnabled(!!res.data.enabled);
      setUpdatedAt(res.data.updated_at || null);
    } catch (err) {
      console.error('Failed to load shifts setting', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const toggle = async (next) => {
    setSaving(true);
    try {
      const res = await api.put('/settings/multi-cashier-shifts', { enabled: next });
      setEnabled(!!res.data.enabled);
      toast.success(
        next
          ? 'Multi-Cashier Shifts ENABLED. Cashiers will be prompted to open a shift on their next sale.'
          : 'Multi-Cashier Shifts disabled. Sales will resume the legacy flow immediately.'
      );
      load();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.code === 'open_shifts_exist') {
        toast.error(`Cannot disable: ${detail.open_count} shift(s) are still open. Close them first.`);
      } else {
        toast.error(typeof detail === 'string' ? detail : detail?.message || 'Failed to update setting.');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-sm text-slate-500">Loading…</div>;
  }

  return (
    <div className="space-y-6" data-testid="shifts-settings-panel">
      <div className="flex items-start justify-between gap-4 p-4 border border-slate-200 rounded-lg bg-white">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-md ${enabled ? 'bg-emerald-50' : 'bg-slate-100'}`}>
            <ClipboardCheck size={18} className={enabled ? 'text-emerald-700' : 'text-slate-500'} />
          </div>
          <div>
            <Label htmlFor="shifts-toggle" className="text-base font-semibold">
              Multi-Cashier Shifts
            </Label>
            <p className="text-sm text-slate-600 mt-1 max-w-2xl">
              Track per-cashier accountability for cash handled, sales rung, and variance.
              Each cashier opens a shift with an authorized float at the start of their shift,
              and closes it with a cash count — generating an X-Report (Phase 2).
            </p>
            {updatedAt && (
              <p className="text-[11px] text-slate-400 mt-1.5">
                Last updated: {new Date(updatedAt).toLocaleString()}
              </p>
            )}
          </div>
        </div>
        <Switch
          id="shifts-toggle"
          data-testid="shifts-toggle"
          checked={enabled}
          disabled={saving}
          onCheckedChange={toggle}
        />
      </div>

      {/* What changes — at-a-glance */}
      <div className={`p-4 rounded-lg border ${enabled ? 'bg-emerald-50/40 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}>
        <div className="flex items-center gap-2 mb-2">
          <Info size={14} className={enabled ? 'text-emerald-700' : 'text-slate-500'} />
          <span className="text-sm font-semibold">
            {enabled ? 'What changes for cashiers right now' : 'What turning this ON will do'}
          </span>
        </div>
        <ul className="text-sm text-slate-700 space-y-1.5 ml-6 list-disc">
          <li>On their next sale, cashiers will be blocked until they open a shift with a manager PIN.</li>
          <li>Every sale and wallet movement they create will be tagged to their shift.</li>
          <li>The Z-Report and Close Wizard will behave the same as today (per-cashier breakdown ships in Phase 3).</li>
          <li>You can turn this OFF at any time as long as no shift is currently open — operations return to the legacy flow instantly.</li>
        </ul>
      </div>

      {/* Guardrails warning */}
      <div className="p-3 rounded-md bg-amber-50 border border-amber-200 flex items-start gap-2">
        <AlertTriangle size={14} className="text-amber-700 mt-0.5 shrink-0" />
        <p className="text-xs text-amber-800">
          <strong>Safety guardrail:</strong> You cannot disable this feature while any cashier shift is still <em>open</em>.
          Close all open shifts first (Phase 2: from the header badge, the cashier closes their own; an admin can force-close in Phase 4).
        </p>
      </div>
    </div>
  );
}
